/**
 * Ops Resource Ontology desk (RF-ONT-1.1 / RES-1 L0).
 * Mount: /ops/resources · alias /resources
 * Live buses: resource-ontology status + projection.
 *
 * One job: typed Resources workspace (People / Cameras / Robots / Equipment)
 * with Places + Teams as independent scopes. CTAs navigate only — never invent writes.
 */
import { useEffect, useMemo, useState } from "react";
import { html, Alert, Ico, KV, M, SOFT, SPRING } from "./ui.js";
import { useAction, usePolled } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { confirmLeaveSeat } from "./shell-wayfind.js";
import { formatUserInstant } from "./time.js";

const TYPE_VIEWS = [
  { id: "any", label: "All" },
  { id: "people", label: "People" },
  { id: "camera", label: "Cameras" },
  { id: "robot", label: "Robots" },
  { id: "equipment", label: "Equipment" },
];

const RESOURCE_TYPE_ALIASES = Object.freeze({
  person: "people",
  people: "people",
  responder: "people",
  camera: "camera",
  robot: "robot",
  external_robot: "robot",
  equipment: "equipment",
  device: "equipment",
  edge_device: "equipment",
});

const READINESS_ORDER = Object.freeze({
  unavailable: 0,
  degraded: 1,
  coverage_gap: 2,
  busy: 3,
  available: 4,
  unknown: 5,
  stale: 6,
});

function deviceIdOf(resourceId) {
  const s = String(resourceId || "");
  if (s.startsWith("device:")) return s.slice(7);
  return "";
}

function resourceTypeOf(r) {
  const kind = String(
    (r && (r.resource_type || r.resource_kind || r.kind || r.type)) || "",
  ).toLowerCase().trim();
  return RESOURCE_TYPE_ALIASES[kind] || "unknown";
}

function hashQueryOf() {
  try {
    const h = String((typeof location !== "undefined" && location.hash) || "");
    const qi = h.indexOf("?");
    if (qi < 0) return new URLSearchParams();
    return new URLSearchParams(h.slice(qi + 1));
  } catch {
    return new URLSearchParams();
  }
}

function readinessStateOf(resource) {
  const declared = String((resource && resource.readiness && resource.readiness.state) || "").trim();
  if (Object.prototype.hasOwnProperty.call(READINESS_ORDER, declared)) return declared;
  const live = (resource && resource.live_state) || {};
  if (live.stale === true || live.fresh === false) return "stale";
  if (live.available === true) return "available";
  if (live.online === false) return "unavailable";
  return "unknown";
}

function observedLabelOf(resource) {
  const raw = (resource && resource.live_state && resource.live_state.last_seen) || null;
  if (!raw) return "No observation time";
  const label = formatUserInstant(raw, { seconds: false });
  return label === "—" ? "Observation time unavailable" : "Observed " + label;
}

function replaceResourceQuery(type, view) {
  if (typeof location === "undefined" || typeof history === "undefined") return;
  const params = new URLSearchParams();
  if (type && type !== "any") params.set("type", type);
  if (view === "readiness") params.set("view", "readiness");
  const nextHash = "#/ops/resources" + (params.toString() ? "?" + params.toString() : "");
  history.replaceState(null, "", location.pathname + location.search + nextHash);
}

export function OpsResourceOntologyDesk({ toast }) {
  const st = usePolled("/ops/resource-ontology/status", 30000);
  const body = usePolled("/ops/resource-ontology", 20000);
  const soakAct = useAction();
  const envelope = soakAct.envelope;
  const q0 = hashQueryOf();
  const initialType = TYPE_VIEWS.some((t) => t.id === q0.get("type")) ? q0.get("type") : "any";
  const [typeView, setTypeView] = useState(initialType);
  const [viewMode, setViewMode] = useState(q0.get("view") === "readiness" ? "readiness" : "inventory");
  const [capFilter, setCapFilter] = useState("any");
  const [soakNotice, setSoakNotice] = useState("");

  useEffect(() => {
    const syncHash = () => {
      const query = hashQueryOf();
      const nextType = TYPE_VIEWS.some((t) => t.id === query.get("type"))
        ? query.get("type")
        : "any";
      setTypeView(nextType);
      setViewMode(query.get("view") === "readiness" ? "readiness" : "inventory");
    };
    if (typeof window !== "undefined") window.addEventListener("hashchange", syncHash);
    return () => {
      if (typeof window !== "undefined") window.removeEventListener("hashchange", syncHash);
    };
  }, []);

  // Wave 23 — record Live Resources day-use for §3.2 soak (never Soft invent pass).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out = await soakAct.run("/ops/resource-ontology/soak/ping", { method: "POST", body: {} });
      if (cancelled) return;
      if (!out) {
        setSoakNotice("Usage evidence could not be recorded for this seat.");
        return;
      }
      setSoakNotice("");
      st.reload && st.reload();
    })();
    return () => { cancelled = true; };
  }, []);

  const status = st.data || {};
  const proj = body.data || {};
  const armed = !!(status.enabled || status.armed || proj.ok);
  const zones = Array.isArray(proj.zones) ? proj.zones : [];
  const groups = Array.isArray(proj.groups) ? proj.groups : [];
  const resources = Array.isArray(proj.resources) ? proj.resources : [];
  const cands = ((proj.ai_resolver || {}).candidates) || [];
  const live = proj.live_state_summary || {};
  const viz = proj.visualization || {};

  const typed = useMemo(() => {
    let rows = resources;
    if (typeView && typeView !== "any") {
      rows = rows.filter((r) => resourceTypeOf(r) === typeView);
    }
    if (capFilter && capFilter !== "any") {
      rows = rows.filter((r) => (r.capabilities || []).includes(capFilter));
    }
    return [...rows].sort((a, b) => {
      const aState = readinessStateOf(a);
      const bState = readinessStateOf(b);
      return (READINESS_ORDER[aState] ?? 99) - (READINESS_ORDER[bState] ?? 99)
        || String(a.label || a.resource_id).localeCompare(String(b.label || b.resource_id));
    });
  }, [resources, typeView, capFilter]);

  const typeCounts = useMemo(() => {
    const out = {
      any: resources.length,
      people: 0,
      camera: 0,
      robot: 0,
      equipment: 0,
      unknown: 0,
    };
    for (const r of resources) {
      const t = resourceTypeOf(r);
      if (Object.prototype.hasOwnProperty.call(out, t)) out[t] += 1;
      else out.unknown += 1;
    }
    return out;
  }, [resources]);

  const availableTyped = typed.filter((r) => (r.live_state || {}).available === true).length;
  const busyTyped = typed.filter((r) => readinessStateOf(r) === "busy").length;
  const degradedTyped = typed.filter((r) => readinessStateOf(r) === "degraded").length;
  const staleTyped = typed.filter((r) => {
    const ls = r.live_state || {};
    return ls.stale === true || ls.fresh === false;
  }).length;
  const readinessFocus = viewMode === "readiness";
  const excluded = ((proj.ai_resolver || {}).excluded) || [];

  const chooseType = (nextType) => {
    setTypeView(nextType);
    replaceResourceQuery(nextType, viewMode);
  };

  const chooseView = (nextView) => {
    setViewMode(nextView);
    replaceResourceQuery(typeView, nextView);
  };

  const here = "/ops/resources";
  const leave = (target) => confirmLeaveSeat({ fromRoute: here, targetPath: target });

  const openCams = (resourceId) => {
    const did = deviceIdOf(resourceId);
    try {
      leave(did ? `/ops/cameras?device=${encodeURIComponent(did)}` : "/ops/cameras");
      toast && toast(did ? `Opening cameras for ${did}` : "Opening cameras");
    } catch {
      toast && toast("Could not open cameras");
    }
  };

  const openDispatch = (resourceId) => {
    try {
      leave("/ops/dispatch");
      toast && toast("Opening dispatch. Confirm the resource again before sending.");
    } catch {
      toast && toast("Could not open dispatch");
    }
  };

  const openAssign = (resourceId) => {
    try {
      leave("/response/assign");
      toast && toast("Opening assignment. Confirm the resource again in Response.");
    } catch {
      toast && toast("Could not open assignment");
    }
  };

  const openMachine = (resourceId) => {
    const did = deviceIdOf(resourceId);
    if (!did) return;
    try {
      leave(`/machine/${encodeURIComponent(did)}`);
    } catch {
      toast && toast("Could not open the machine record");
    }
  };

  const saveReport = () => {
    try {
      const report = {
        schema: "resource_readiness_report_v1",
        saved_at: new Date().toISOString(),
        tenant_id: proj.tenant_id || null,
        type_view: typeView,
        live_state_summary: live,
        zones,
        groups,
        resources: typed,
        ai_resolver: proj.ai_resolver || null,
        visualization: {
          node_count: (viz.nodes || []).length,
          edge_count: (viz.edges || []).length,
        },
      };
      const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `resource-readiness-report-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast && toast("Readiness report downloaded");
    } catch {
      toast && toast("Could not save report");
    }
  };

  if ((st.loading || body.loading) && !st.data && !body.data) {
    return html`<div className="sheet ops-onto-desk ops-onto-desk--liquid desk-craft"
      data-cmc-page="ops-resource-ontology" data-cmc-res="res-1"
      data-cmc-shell="resources-ux" aria-busy="true">
      <div className="ops-onto__loading" role="status" aria-live="polite">
        <span className="ops-onto__pulse"/>
        <strong>Reading resource readiness…</strong>
        <span>People, cameras, robots, and equipment stay separate until each source answers.</span>
      </div>
    </div>`;
  }

  if ((st.error || status.ok === false) && !armed) {
    return html`<div className="sheet ops-onto-desk ops-onto-desk--liquid desk-craft" data-cmc-page="ops-resource-ontology" data-cmc-res="res-1" data-cmc-shell="resources-ux">
      <${Alert} tone="warn" title="Resource readiness needs access"
        say="This account cannot read operational resources. Ask a manager for resource-read access, then re-open this workspace."/>
    </div>`;
  }

  return html`<${M.div} className="sheet ops-onto-desk ops-onto-desk--liquid desk-craft"
      data-cmc-page="ops-resource-ontology"
      data-cmc-res="res-1"
      data-cmc-shell="resources-ux"
      initial=${SOFT} animate=${{ opacity:1, y:0 }} transition=${SPRING}>
    <header className="ops-onto__hero">
      <div>
        <p className="eyebrow">Operations · Resources</p>
        <h2>Who and what can respond now?</h2>
        <p className="note">Sort by readiness, then narrow by type or verified skill. Simulation rows stay marked SIM and never enter recommendations.</p>
      </div>
      <div className="ops-onto__hero-state" data-state=${body.error || proj.ok === false ? "degraded" : "ready"}>
        <span>${body.error || proj.ok === false ? "Partial" : "Connected"}</span>
        <strong>${typed.length}</strong>
        <em>in current view</em>
      </div>
    </header>

    ${body.error || proj.ok === false
      ? html`<${Alert} tone="warn" title="Some resource sources did not answer"
          say="Healthy resource types remain visible. Re-read to retry the missing source; nothing is filled with estimated availability."/>`
      : null}
    ${soakNotice
      ? html`<${Alert} tone="info" title="Usage evidence not recorded" say=${soakNotice}/>`
      : null}
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <div className="chips" style=${{ marginBottom: 12, flexWrap: "wrap", gap: 8 }} role="tablist" aria-label="Resource types">
      ${TYPE_VIEWS.map((t) => html`<button key=${t.id} type="button" className="chip"
        role="tab" data-tone=${typeView === t.id ? "go" : "ai"}
        aria-selected=${typeView === t.id ? "true" : "false"}
        onClick=${() => chooseType(t.id)}>
        ${t.label}${t.id === "any" ? ` · ${typeCounts.any}` : ` · ${typeCounts[t.id] || 0}`}
      </button>`)}
      <button type="button" className="chip" data-tone=${readinessFocus ? "go" : "ai"}
        aria-pressed=${readinessFocus ? "true" : "false"}
        onClick=${() => chooseView(readinessFocus ? "inventory" : "readiness")}>Readiness view</button>
    </div>

    <section className="ops-onto__sec ops-onto__readiness" data-cmc-res="res-2" style=${{ marginBottom: 14 }}>
      <div className="ops-onto__sec-head">
        <div>
          <h3><${Ico} d="gauge" w=${14}/> Readiness now</h3>
          <p className="muted">Stale observations never count as available. Check the observation time before handing work off.</p>
        </div>
        <span className="ops-onto__observed">${live.last_observed_at
          ? "Latest observation · " + String(live.last_observed_at)
          : "No source observation time"}</span>
      </div>
      <div className="ops-onto__kpis" style=${{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 8, marginBottom: 10 }}>
        ${[
          ["In view", typed.length],
          ["Available", availableTyped],
          ["Busy", busyTyped],
          ["Degraded", degradedTyped],
          ["Stale", staleTyped],
          ["SIM", live.simulated_presence_devices ?? 0],
        ].map(([k, v]) => html`<div className="ops-onto__kpi" key=${"r-"+k}>
          <em>${k}</em><strong>${v}</strong>
        </div>`)}
      </div>
      <p className="muted" style=${{ marginBottom: 8 }}>
        ${readinessFocus
          ? "Readiness view puts unavailable and degraded resources first, followed by busy, available, unknown, and stale."
          : "This view reports operational readiness only. Drills and incident assignments remain in Response."}
      </p>
      <div className="btn-row ops-onto__handoffs" style=${{ flexWrap: "wrap", gap: 8 }}>
        <button type="button" className="btn btn--go btn--sm" onClick=${() => leave("/response/assign")}>Leave Resources · Prepare assignment</button>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${() => leave("/response/cases")}>Leave Resources · Mission Room</button>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${() => leave("/response/readiness")}>Leave Resources · Drill readiness</button>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${() => leave("/machine")}>Leave Resources · Machine</button>
      </div>
    </section>

    <section className="ops-onto__sec ops-onto__evidence" data-cmc-res="res-3" style=${{ marginBottom: 14 }}>
      <h3><${Ico} d="layers" w=${14}/> Workspace evidence</h3>
      <p className="note">Resources remains inside Operations while reliability evidence is incomplete.</p>
      <ul className="ops-onto__list" style=${{ marginTop: 8 }} aria-live="polite"
        data-cmc-res3-gate=${(status.graduation && status.graduation.all_gates_pass && status.res3_promote_authorized) ? "ready" : "pending"}
        data-cmc-res3-promote=${status.res3_promote_authorized === true ? "1" : "0"}>
        ${(["freshness","operate","stale","handoff","soak","ownership"]).map((gid) => {
          const g = (status.graduation && status.graduation.gates && status.graduation.gates[gid]) || {};
          const ok = g.pass === true;
          const labels = {
            freshness: "Three resource types have fresh observations",
            operate: "Two actions have verified receipts",
            stale: "Stale resources are excluded",
            handoff: "Assignment handoff keeps its context",
            soak: "Used on three separate days",
            ownership: "Machine and Response records stay separate",
          };
          return html`<li key=${gid} data-gate=${gid} data-pass=${ok ? "1" : "0"}>
            <strong>${labels[gid] || gid}</strong>
            <span className="muted"> · ${ok ? "verified" : "needs evidence"}</span>
          </li>`;
        })}
      </ul>
    </section>

    <div className="btn-row" style=${{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
      <button type="button" className="btn btn--ghost" onClick=${() => { st.reload && st.reload(); body.reload && body.reload(); }}>Re-read</button>
      <button type="button" className="btn btn--go" onClick=${saveReport}>Download readiness report</button>
      <label className="ops-onto__filter">Verified skill
        <select value=${capFilter} onChange=${(e) => setCapFilter(e.target.value)}>
          <option value="any">Any</option>
          <option value="snapshot">Camera capture</option>
          <option value="siren">Siren</option>
          <option value="drone">Drone</option>
          <option value="ground_robot">Ground robot</option>
        </select>
      </label>
    </div>

    <div className="ops-onto__kpis" style=${{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 10, marginBottom: 16 }}>
      ${[
        ["Places", live.zones ?? zones.length],
        ["Teams", live.groups ?? groups.length],
        ["Resources", live.resources ?? resources.length],
        ["Links", live.relationships ?? 0],
        ["Available", live.available_resources ?? availableTyped],
      ].map(([k, v]) => html`<div className="ops-onto__kpi" key=${k}>
        <em>${k}</em><strong>${v}</strong>
      </div>`)}
    </div>

    <section className="ops-onto__sec">
      <h3><${Ico} d="layers" w=${14}/> Places · Where?</h3>
      ${!zones.length
        ? html`<p className="muted">No surveyed places yet.</p>`
        : html`<ul className="ops-onto__list">${zones.map((z) => html`<li key=${z.zone_id}>
            <strong>${z.label || z.zone_id}</strong>
            <span className="muted"> · ${z.device_count || 0} resources inside</span>
          </li>`)}</ul>`}
    </section>

    <section className="ops-onto__sec">
      <h3><${Ico} d="route" w=${14}/> Teams · Who?</h3>
      ${!groups.length
        ? html`<p className="muted">No team scopes on resources yet.</p>`
        : html`<ul className="ops-onto__list">${groups.map((g) => html`<li key=${g.group_id}>
            <strong>${g.group_id}</strong>
            <span className="muted"> · ${(g.resource_ids || []).length} resources</span>
          </li>`)}</ul>`}
    </section>

    <section className="ops-onto__sec">
      <h3><${Ico} d="chip" w=${14}/> Resources</h3>
      ${!typed.length
        ? html`<p className="muted">${!resources.length
          ? "No operational resources are registered for this account. Add people in Settings, cameras in Camera settings, or machines in Activate."
          : "No resources match this type and verified-skill filter. Clear one filter to widen the view."}</p>`
        : html`<ul className="ops-onto__list ops-onto__resource-list">${typed.slice(0, 40).map((r, index) => {
          const readiness = readinessStateOf(r);
          return html`<${M.li} key=${r.resource_id} className="ops-onto__resource"
              data-readiness=${readiness}
              initial=${{ opacity: 0, y: 8 }}
              animate=${{ opacity: 1, y: 0 }}
              transition=${{ ...SPRING, delay: Math.min(index, 12) * 0.025 }}>
            <div className="ops-onto__resource-main">
              <span className="ops-onto__resource-type">${resourceTypeOf(r)}</span>
              <strong>${r.label || r.resource_id}</strong>
              <span className="ops-onto__state" data-state=${readiness}>${readiness.replace("_", " ")}</span>
              ${(r.live_state || {}).simulated_presence
                ? html`<span className="ops-onto__sim" data-cmc-sim="1">SIM</span>`
                : null}
            </div>
            <div className="ops-onto__resource-facts">
              <span>${(r.capabilities || []).join(" · ") || "No verified skills"}</span>
              <span>${observedLabelOf(r)}</span>
              ${r.group_scope ? html`<span>Team · ${r.group_scope}</span>` : null}
              ${r.assignment ? html`<span>Assigned · ${String(r.assignment)}</span>` : null}
            </div>
            <div className="btn-row ops-onto__row-actions">
              ${deviceIdOf(r.resource_id)
                ? html`<button type="button" className="btn btn--ghost btn--sm"
                    onClick=${() => openMachine(r.resource_id)}>Leave Resources · Machine</button>`
                : null}
              ${resourceTypeOf(r) === "camera"
                ? html`<button type="button" className="btn btn--ghost btn--sm"
                    onClick=${() => openCams(r.resource_id)}>Leave Resources · Camera view</button>`
                : null}
              <button type="button" className="btn btn--ghost btn--sm"
                onClick=${() => openDispatch(r.resource_id)}>Leave Resources · Dispatch</button>
            </div>
          <//>`;
        })}</ul>`}
    </section>

    <section className="ops-onto__sec">
      <h3><${Ico} d="gauge" w=${14}/> Recommended resources</h3>
      ${!cands.length
        ? html`<p className="muted">No verified, fresh camera resource is ready. ${excluded.length
          ? `${excluded.length} capability matches were excluded because they are stale, unavailable, policy-blocked, or simulation-only.`
          : "Register a verified camera skill and wait for a fresh observation."}</p>`
        : html`<ol className="ops-onto__list ops-onto__recommendations">${cands.slice(0, 12).map((c) => html`<li key=${c.resource_id}>
            <strong>${c.resource_id}</strong>
            <span className="muted"> · ${c.reason || "Verified capability and readiness"} · score ${c.score}</span>
            ${deviceIdOf(c.resource_id) ? html`<span className="btn-row" style=${{ display: "inline-flex", gap: 6, marginLeft: 8 }}>
              <button type="button" className="btn btn--go" onClick=${() => openCams(c.resource_id)}>Leave Resources · Camera view</button>
              <button type="button" className="btn btn--ghost" onClick=${() => openAssign(c.resource_id)}>Leave Resources · Prepare assignment</button>
            </span>` : null}
          </li>`)}</ol>`}
      <p className="muted" style=${{ marginTop: 8 }}>Recommendations use verified capability, current readiness, policy, and Area membership. They never create an assignment here.</p>
    </section>
  <//>`;
}

export default OpsResourceOntologyDesk;
